import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Shared visitor-country state. Performs ONE fetch per session (cached in
 * sessionStorage) and exposes the detected country + per-language phrases
 * to every component that needs them, Hero, Footer, About copy, the
 * Shipping Ribbon, etc.
 *
 * Default fallback (before fetch resolves or on failure) returns the neutral
 * "Worldwide" copy so the UI never flickers between Spain-centric strings
 * and the dynamic ones.
 */

const SESSION_KEY = 'plp_geo_country';

const FALLBACK = {
  country_code: null,
  names: { en: 'Worldwide', es: 'Todo el mundo', ru: 'по всему миру' },
  delivery_phrase: {
    en: 'Worldwide shipping',
    es: 'Envío a todo el mundo',
    ru: 'Доставка по всему миру',
  },
  in_phrase: {
    en: 'worldwide',
    es: 'a nivel mundial',
    ru: 'по всему миру',
    de: 'weltweit',
    fr: 'dans le monde entier',
    it: 'in tutto il mondo',
    pl: 'na całym świecie',
    nl: 'wereldwijd',
    pt: 'em todo o mundo',
  },
  source: 'unknown',
};

const GeoCountryContext = createContext(FALLBACK);

export const useGeoCountry = () => useContext(GeoCountryContext);

/** Convenience hook, returns just the localized "in {country}" phrase. */
export const useInPhrase = (currentLanguage) => {
  const geo = useGeoCountry();
  const supported = ['en', 'es', 'ru', 'de', 'fr', 'it', 'pl', 'nl', 'pt'];
  const lang = supported.includes(currentLanguage) ? currentLanguage : 'en';
  return geo?.in_phrase?.[lang] || FALLBACK.in_phrase[lang] || FALLBACK.in_phrase.en;
};

export const GeoCountryProvider = ({ children }) => {
  const [geo, setGeo] = useState(() => {
    try {
      const cached = sessionStorage.getItem(SESSION_KEY);
      if (cached) return { ...FALLBACK, ...JSON.parse(cached) };
    } catch (_) {}
    return FALLBACK;
  });

  useEffect(() => {
    let cancelled = false;
    // If we already have a real country code from cache, skip the network hit.
    if (geo?.country_code) return undefined;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/geo/country`;
    axios.get(url, { timeout: 4000 }).then((r) => {
      if (cancelled || !r?.data) return;
      const merged = { ...FALLBACK, ...r.data };
      setGeo(merged);
      try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(merged)); } catch (_) {}
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GeoCountryContext.Provider value={geo}>
      {children}
    </GeoCountryContext.Provider>
  );
};
